import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import imageBlockSVG from '@plone/volto/components/manage/Blocks/Image/block-image.svg';
import type {
  TeaserBlockDefaultProps,
  TeaserBlockData,
  TeaserCardType,
  NormalizedTeaserCard,
} from '@fhnw/types/teaserBlocksDefault';
import { teaserBlockDefaultMessages } from './TeaserBlockDefaultMessages';
import { Card, CardProps } from '@designsystem/stories/03-molecules/Card';
import { ButtonProps } from '@designsystem/stories/02-atoms/Button';
import {
  normalizeContent,
  normalizeLink,
} from '@fhnw/helpers/DesignSystem/normalizeContent';
import { transformToCard } from '@fhnw/helpers/DesignSystem/cardTransformers';
import type { NormalizableContent } from '@fhnw/helpers/DesignSystem/types';
import type { MediaProps } from '@designsystem/stories/02-atoms/Media';
import { getListingImageSizes } from '@fhnw/helpers/DesignSystem/imageSizes';

/**
 * Maps teaser block variations to card types
 */
const VARIATION_TO_CARD_TYPE: Record<string, TeaserCardType> = {
  infobox: 'teaser',
  fullBleedTeaser: 'teaser-fullbleed',
};

/**
 * Determines the card type based on the block variation
 */
const getCardType = (variation?: string): TeaserCardType => {
  if (!variation) return 'none';
  return VARIATION_TO_CARD_TYPE[variation] ?? 'none';
};

/**
 * Normalizes actions from block data to ButtonProps array
 * @param actions - Raw actions from block data
 * @returns Normalized ButtonProps array or undefined
 */
const normalizeActions = (
  actions: ButtonProps[] | undefined,
): ButtonProps[] | undefined => {
  if (!actions || !Array.isArray(actions)) return undefined;

  const normalized = normalizeContent(actions, true);

  // normalizeContent returns the array mapped through normalizeContent
  if (Array.isArray(normalized)) {
    return normalized as ButtonProps[];
  }

  return undefined;
};

/**
 * Applies overwrite values from block data to the card
 */
const applyOverwrites = (
  card: NormalizedTeaserCard,
  data: TeaserBlockData,
): NormalizedTeaserCard => {
  const updatedCard = { ...card };

  if (data.title) {
    updatedCard.title = data.title;
  }
  if (data.description) {
    updatedCard.description = data.description;
  }
  if (data.head_title) {
    updatedCard.category = data.head_title;
  }
  if (data.preview_image?.[0]) {
    const normalizedImage = normalizeContent(
      { image: data.preview_image[0] },
      false,
      getListingImageSizes(card.type as string, undefined, true),
    ) as any;
    if (typeof normalizedImage === 'object' && normalizedImage !== null) {
      updatedCard.image = normalizedImage as MediaProps;
    }
  }
  if (data.custom_href) {
    if (data.custom_href.startsWith('?')) {
      updatedCard.link = (updatedCard.link ?? '') + data.custom_href;
    } else {
      updatedCard.link = normalizeLink(data.custom_href);
    }
  }

  return updatedCard;
};

/**
 * Applies visibility flags to show/hide card elements
 */
const applyVisibilityFlags = (
  card: NormalizedTeaserCard,
  data: TeaserBlockData,
): NormalizedTeaserCard => {
  const updatedCard = { ...card };
  const showTeaserHeading = data.showTeaserHeading ?? false;
  const showTeaserText = data.showTeaserText ?? false;
  const showTeaserImage = data.showTeaserImage ?? false;

  if (!showTeaserHeading) {
    delete updatedCard.title;
  }
  if (!showTeaserText) {
    delete updatedCard.description;
  }

  // Check if image has valid children (rendered content)
  const hasValidImage =
    showTeaserImage &&
    updatedCard.image &&
    'children' in updatedCard.image &&
    updatedCard.image.children;

  if (!hasValidImage) {
    delete updatedCard.image;
  }

  return updatedCard;
};

/**
 * Applies image configuration (aspect ratio and focus point)
 */
const applyImageConfig = (
  card: NormalizedTeaserCard,
  data: TeaserBlockData,
): NormalizedTeaserCard => {
  if (!card.image) return card;

  const updatedCard = { ...card };
  if (updatedCard.image) {
    updatedCard.image = {
      ...updatedCard.image,
      aspect: data.format as MediaProps['aspect'],
      focus_point: data.focus_point as MediaProps['focus_point'],
    };
  }

  return updatedCard;
};

/**
 * TeaserBlockDefault - Renders a teaser block as a Card component
 *
 * @description Transforms Plone content data into a Design System Card.
 * Supports different variations (infobox, fullBleedTeaser) and allows
 * overwriting title, description, image, and link.
 */
const TeaserBlockDefault: React.FC<TeaserBlockDefaultProps> = (props) => {
  const { data, isEditMode } = props;
  const intl = useIntl();

  // Get the first selected content item
  const item: NormalizableContent | undefined = data?.href?.[0];

  // Memoize card type calculation
  const cardType = useMemo(
    () => getCardType(data?.variation),
    [data?.variation],
  );

  // Memoize the entire card transformation
  const card = useMemo((): CardProps | null => {
    if (!item) return null;

    // Get base card from transformToCard (includes type, title, description, image, link)
    const baseCard = transformToCard(
      item,
      intl,
      cardType,
      undefined,
      undefined,
      false,
      getListingImageSizes(cardType, undefined, true),
    );

    // Create normalized card with actions and link override
    let normalizedCard: NormalizedTeaserCard = {
      ...baseCard,
      actions: normalizeActions(data?.actions),
      link: normalizeLink(item),
    };

    // Apply overwrites if enabled
    if (data?.overwrite === true) {
      normalizedCard = applyOverwrites(normalizedCard, data);
    }

    // Apply visibility flags
    normalizedCard = applyVisibilityFlags(normalizedCard, data);

    // Apply image configuration
    normalizedCard = applyImageConfig(normalizedCard, data);

    // CardProps requires 'type' which is provided by transformToCard/baseCard
    return normalizedCard as CardProps;
  }, [item, data, intl, cardType]);

  // Render placeholder in edit mode when no content is selected
  if (!item) {
    return (
      <>
        {isEditMode && (
          <div className="teaser-item placeholder">
            <img src={imageBlockSVG} alt="" />
            <p>
              {intl.formatMessage(
                teaserBlockDefaultMessages.PleaseChooseContent,
              )}
            </p>
          </div>
        )}
      </>
    );
  }

  // Safety check - should not happen if item exists
  if (!card) return null;

  return <Card {...card} />;
};

export default TeaserBlockDefault;
